/**
 * Radiance Collection
 * Дані продуктів зчитуються з <script type="application/json" data-product-json>
 * Нуль fetch запитів — все inline в HTML
 */

class RadianceCollection {
  constructor(section) {
    this.section         = section;
    this.swatches        = [...section.querySelectorAll('.radiance-collection__swatch')];
    this.cards           = [...section.querySelectorAll('.radiance-collection__product-card')];
    this.productsEl      = section.querySelector('.radiance-collection__products');
    this.activeColor     = this.swatches[0]?.dataset.color ?? null;

    // Парсимо JSON один раз при ініціалізації
    this.productDataMap  = this.parseProductData();

    this.init();
  }

  // ─── PARSE JSON ──────────────────────────────────────────────────────────

  parseProductData() {
    const map = new Map();

    this.cards.forEach(card => {
      const scriptEl = card.querySelector('[data-product-json]');
      if (!scriptEl) return;

      try {
        const data = JSON.parse(scriptEl.textContent);
        map.set(card, data);
      } catch (e) {
        console.warn('[RadianceCollection] Failed to parse product JSON', e);
      }
    });

    return map;
  }

  // ─── INIT ────────────────────────────────────────────────────────────────

  init() {
    this.bindSwatches();
    this.bindKeyboard();
  }

  // ─── SWATCHES ────────────────────────────────────────────────────────────

  bindSwatches() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        const color = swatch.dataset.color;
        if (color === this.activeColor) return;
        this.switchColor(color, swatch);
      });
    });
  }

  switchColor(color, activeSwatch) {
    this.activeColor = color;

    // Оновлюємо стан swatches
    this.swatches.forEach(s => {
      const isActive = s === activeSwatch;
      s.classList.toggle('is-active', isActive);
      s.setAttribute('aria-pressed', String(isActive));
    });

    // Оновлюємо кожну картку
    this.cards.forEach(card => this.updateCard(card, color));
  }

  // ─── UPDATE CARD ─────────────────────────────────────────────────────────

  updateCard(card, targetColor) {
    const productData = this.productDataMap.get(card);
    if (!productData) return;

    const colorOption = card.dataset.colorOption;
    const variant = this.findVariant(productData, colorOption, targetColor);

    if (!variant) return;

    this.updateImage(card, variant, productData.images);
    this.updatePrice(card, variant);
  }

  // Прибираємо суфікс літражу з назви кольору: "Peony Pink 710ml" → "peony pink"
  normalizeColor(name) {
    if (!name) return '';
    return String(name)
      .replace(/\s*\d+(?:[.,]\d+)?\s*(?:ml|l|oz|cl)\b/gi, '')
      .trim()
      .toLowerCase();
  }

  findVariant(productData, colorOptionName, targetColor) {
    const optionIndex = productData.options.findIndex(
      opt => opt.toLowerCase() === colorOptionName.toLowerCase()
    );

    // Немає такої опції — повертаємо перший доступний
    if (optionIndex === -1) {
      return productData.variants.find(v => v.available)
        ?? productData.variants[0]
        ?? null;
    }

    const optionKey = `option${optionIndex + 1}`;
    const target = this.normalizeColor(targetColor);

    const matchesColor = v => this.normalizeColor(v[optionKey]) === target;

    // Спочатку шукаємо доступний варіант з цим кольором, потім будь-який
    return productData.variants.find(v => matchesColor(v) && v.available)
      ?? productData.variants.find(matchesColor)
      ?? null;
  }

  // ─── IMAGE SWAP ──────────────────────────────────────────────────────────

  updateImage(card, variant, productImages) {
    const img = card.querySelector('.radiance-collection__product-img');
    const secondaryImgEl = card.querySelector('.radiance-collection__product-img--secondary');
    if (!img) return;

    // Немає нового зображення — нічого не міняємо
    if (!variant.featured_image?.src) return;

    // Вже показуємо цей варіант
    if (img.dataset.currentVariantId === String(variant.id)) return;

    const newSrc = this.buildImageUrl(variant.featured_image.src, 500);

    this.setLoading(card, true);

    const preload = new Image();
    preload.onload = () => {
      img.src = newSrc;
      img.alt = variant.title ?? img.alt;
      img.dataset.currentVariantId = String(variant.id);
      this.setLoading(card, false);
    };
    preload.onerror = () => this.setLoading(card, false);
    preload.src = newSrc;

    if (secondaryImgEl) {
      const secondaryImg = productImages.filter(img => img.alt === variant.title);
      if (secondaryImg.length > 0) {
        const newSecondarySrc = this.buildImageUrl(secondaryImg[0].src, 500);
        const preloadSecondary = new Image();
        preloadSecondary.onload = () => {
          secondaryImgEl.src = newSecondarySrc;
          secondaryImgEl.alt = secondaryImg[0].alt ?? secondaryImgEl.alt;
        };
        preloadSecondary.src = newSecondarySrc;
      }
    }
  }

  buildImageUrl(src, width) {
    if (!src) return src;
    // Замінюємо існуючий розмір або додаємо новий
    if (/_\d+x\d*\./.test(src)) {
      return src.replace(/_\d+x\d*\./, `_${width}x.`);
    }
    return src.replace(/(\.\w+)(\?.*)?$/, `_${width}x$1$2`);
  }

  // ─── PRICE ───────────────────────────────────────────────────────────────

  updatePrice(card, variant) {
    const el = card.querySelector('[data-price]');
    if (!el) return;
    el.textContent = this.formatMoney(variant.price);
  }

  formatMoney(cents) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat ?? '£{{amount}}');
    }
    return `£${(cents / 100).toFixed(2)}`;
  }

  // ─── LOADING STATE ───────────────────────────────────────────────────────

  setLoading(card, state) {
    card.classList.toggle('is-loading', state);
  }

  // ─── KEYBOARD ────────────────────────────────────────────────────────────

  bindKeyboard() {
    this.swatches.forEach((swatch, i) => {
      swatch.addEventListener('keydown', e => {
        const total = this.swatches.length;
        let next    = null;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown')  next = (i + 1) % total;
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')    next = (i - 1 + total) % total;

        if (next !== null) {
          e.preventDefault();
          this.swatches[next].focus();
          this.swatches[next].click();
        }
      });
    });
  }
}

// ─── BOOTSTRAP ───────────────────────────────────────────────────────────────

function initAll() {
  document.querySelectorAll('.section-radiance-collection').forEach(section => {
    if (section.dataset.rcInit) return;
    section.dataset.rcInit = '1';
    new RadianceCollection(section);
  });
}

document.addEventListener('DOMContentLoaded', initAll);

if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const s = e.target;
    if (!s.classList.contains('section-radiance-collection')) return;
    delete s.dataset.rcInit;
    new RadianceCollection(s);
  });
}